/**
 * customer-status-meta.ts: label + StatusDot tone per customer status, and the
 * ordered options the E1 dropdown renders.
 *
 * Lives outside `entity-detail/mock.ts` (its old home, imported by live screens)
 * because E1 makes it the app's status vocabulary, which E9 may still reword.
 * No 'prospect' entry: that value never existed on the server.
 *
 * Names + meanings per Gesamtdokumentation Kap. 5.1: leer (kein Kunde) · neu
 * (Lead) · provisorisch (Analysemandat) · aktiv (Brokermandat) · gekündigt ·
 * inaktiv (schreibgeschützt).
 *
 * [Source: apps/api/src/modules/customer-status/customer-status.service.ts#statusLabel]
 * [Source: packages/shared/src/customer.ts#CUSTOMER_STATUS_WIRE_VALUES] (ordering)
 */

import { CUSTOMER_STATUS_WIRE_VALUES } from '@brokly/shared';
import type { StatusTone } from '../../components/ui/StatusDot';
import type { KundeStatus } from '../entity-detail/types';

export const KUNDE_STATUS_META: Record<KundeStatus, { label: string; tone: StatusTone }> = {
  leer: { label: 'Leer', tone: 'neutral' },
  neu: { label: 'Neu', tone: 'info' },
  provisorisch: { label: 'Provisorisch', tone: 'decide' },
  aktiv: { label: 'Aktiv', tone: 'ok' },
  gekuendigt: { label: 'Gekündigt', tone: 'alert' },
  inaktiv: { label: 'Inaktiv', tone: 'neutral' },
};

/** The dropdown options, in lifecycle order (E1). */
export const KUNDE_STATUS_OPTIONS: readonly { value: KundeStatus; label: string }[] =
  CUSTOMER_STATUS_WIRE_VALUES.map((value) => ({ value, label: KUNDE_STATUS_META[value].label }));

/**
 * The two statuses that generate a mandate document instead of writing a status directly
 * (docx Kap. 5.2 / 5.3): provisorisch produces the Analysemandat, aktiv the Brokermandat.
 * Mirrors the api's TARGET_STATUS_TO_MANDATE_KIND, which stays the single source of truth:
 * this list only decides whether a status pick OPENS the mandate wizard, never what it generates.
 * Shared by the customer list and the customer detail screen, so both menus behave the same.
 */
export const MANDATE_TRIGGER_STATUSES: readonly KundeStatus[] = ['provisorisch', 'aktiv'];

/**
 * The status that opens the dialog «Mandat auflösen» instead of writing (P6, Kap. 10.1): the api
 * answers 409 termination_required for a manual move to it, the Auflösung writes it at the Freigabe.
 * Shared by the customer list and the customer detail screen, so both menus behave the same.
 */
export const TERMINATION_TRIGGER_STATUSES: readonly KundeStatus[] = ['gekuendigt'];
